import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { ActivatedRoute } from '@angular/router';
import { SubmissionsListTableV2Component } from 'src/app/component/submissions-list-table-v2/submissions-list-table-v2.component';
import { SubmissionsFilter } from 'src/app/enum/submissions.filter';
import { EVENT_ADMIN_ICONS } from '../../event-admin-icons';

@Component({
  selector: 'app-submission-ranking',
  templateUrl: './submission-ranking.component.html',
  styleUrls: ['./submission-ranking.component.scss']
})
export class SubmissionRankingComponent implements AfterViewInit, OnDestroy {

  // A tabela detém o estado de filtros/campos; o menu do header reusa esse estado.
  @ViewChild(SubmissionsListTableV2Component) rankingTable: SubmissionsListTableV2Component;

  // Cabeçalho da página fixo: como o layout admin (overflow aninhado + min-height:100vh)
  // quebra position:sticky, usamos position:fixed e ajustamos left/width via JS (largura
  // da área de conteúdo, à direita do sidebar). O espaçador reserva a altura no fluxo.
  @ViewChild('pageHeader') private pageHeaderRef: ElementRef<HTMLElement>;
  @ViewChild('headerSpacer') private headerSpacerRef: ElementRef<HTMLElement>;
  // Última geometria aplicada — evita reescrever estilos quando nada mudou.
  private lastHeaderLeft = -1;
  private lastHeaderWidth = -1;
  private headerRafId = 0;
  // Sincroniza left/width do header fixo com a ÁREA DE CONTEÚDO (o host da página, à direita
  // do sidebar). Roda num loop por frame: trocar de layout (Moderno↔Clássico) ou abrir/fechar
  // o menu lateral muda a POSIÇÃO/largura do conteúdo — e um ResizeObserver NÃO dispara em
  // mudança só de posição, nem captura todos os quadros da animação por eventos. Observando a
  // geometria real a cada frame, o header se adapta a tudo (troca de modo, toggle do menu,
  // animações) sem reload. O `top` é CSS (ciente do modo via :host-context). Só reescreve o
  // estilo quando left/width realmente mudam.
  private readonly trackFixedHeader = (): void => {
    const header = this.pageHeaderRef?.nativeElement;
    const spacer = this.headerSpacerRef?.nativeElement;
    if (header && spacer) {
      const rect = this.elementRef.nativeElement.getBoundingClientRect();
      if (rect.left !== this.lastHeaderLeft || rect.width !== this.lastHeaderWidth) {
        header.style.left = `${rect.left}px`;
        header.style.width = `${rect.width}px`;
        // Espaçador reserva a altura do cabeçalho fixo no fluxo.
        spacer.style.height = `${header.offsetHeight}px`;
        this.lastHeaderLeft = rect.left;
        this.lastHeaderWidth = rect.width;
      }
    }
    this.headerRafId = requestAnimationFrame(this.trackFixedHeader);
  };

  // Termo de busca do header, repassado à tabela via [searchTerm].
  public search: string = '';

  // Mapa global de ícones (sidebar + page headers — single source of truth).
  public icons = EVENT_ADMIN_ICONS;

  // Modo da página: 'ranking' (Lista de submissões) ou 'publication' (Status de
  // publicação). Lido de route.data — a MESMA tela serve os dois, dirigida por aqui e
  // pelo input [page] da tabela V2 (color scheme + persistência de colunas/filtros).
  public page: 'ranking' | 'publication' = 'ranking';

  // Config por modo: título, ícone, rótulo do toggle de cor e quais ações exibir.
  private readonly pageConfig = {
    ranking: {
      titleKey: 'admin.event.submissions.submissions-list.title',
      icon: EVENT_ADMIN_ICONS.ranking,
      colorToggleKey: 'admin.event.submissions.submissions-list.color-as-submission-status',
      showNotifyPending: true,
    },
    publication: {
      titleKey: 'admin.event.publication.status.title',
      icon: EVENT_ADMIN_ICONS.publicationStatus,
      colorToggleKey: 'admin.event.publication.status.color-as-publication-status',
      showNotifyPending: false,
    },
  };
  public get config() { return this.pageConfig[this.page]; }

  // Visibilidade dos dois menus flutuantes do header: filtros (de verdade) e
  // colunas (seletores de coluna, no botão "..."). Mutuamente exclusivos.
  public filtersVisible: boolean = false;
  public columnsVisible: boolean = false;

  // Origem ativa do overlay do menu de colunas — pode ser o botão "..." do header
  // (ao lado do filtro) ou o botão "..." do cabeçalho da coluna de numeração da
  // tabela. O menu ancora em quem disparou.
  public activeColumnsOrigin: CdkOverlayOrigin;

  // Snapshot dos valores do form de filtros no momento em que o menu (modal) abre.
  // "Descartar" restaura este estado; só "Aplicar" persiste as mudanças.
  private filtersSnapshot: any = null;

  // Menu de FILTROS: CENTRO do menu alinhado ao centro do botão de filtro
  // (originX/overlayX: center), logo abaixo. Fallbacks: acima, ou à direita.
  public readonly filterMenuPositions: ConnectedPosition[] = [
    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top',    offsetY: 8 },
    { originX: 'center', originY: 'top',    overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
    { originX: 'end',    originY: 'bottom', overlayX: 'end',    overlayY: 'top',    offsetY: 8 },
  ];

  // Menu de COLUNAS: borda ESQUERDA do menu alinhada à esquerda do ícone-gatilho
  // (originX/overlayX: start), logo abaixo. Fallbacks: à direita, ou acima.
  public readonly columnsMenuPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top',    offsetY: 8 },
    { originX: 'end',   originY: 'bottom', overlayX: 'end',   overlayY: 'top',    offsetY: 8 },
    { originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
  ];

  constructor(private elementRef: ElementRef, private ngZone: NgZone, private cdr: ChangeDetectorRef, route: ActivatedRoute) {
    this.page = route.snapshot.data?.page === 'publication' ? 'publication' : 'ranking';
  }

  ngAfterViewInit(): void {
    // O header reusa o ViewChild `rankingTable` (resolvido só agora). Bindings que
    // dependem dele — ex.: o `*ngIf` de prontidão (event/grupos/forms) do botão de Ações —
    // mudam de undefined → valor real dentro deste tick; sem um CD explícito aqui, o
    // dev-mode acusa ExpressionChangedAfterItHasBeenCheckedError. Estabilizamos com um
    // detectChanges() após o ViewChild existir.
    this.cdr.detectChanges();
    // Loop por frame FORA da zona do Angular (só lê geometria e escreve estilo inline; não
    // dispara change detection). Mantém o header fixo alinhado à área de conteúdo o tempo todo.
    this.ngZone.runOutsideAngular(() => {
      this.headerRafId = requestAnimationFrame(this.trackFixedHeader);
    });
    // A Lista de Submissões rola DENTRO do card (max-height + overflow:auto). Acima dele,
    // o único outro scroller vertical é o DOCUMENTO (<html>): sobra um overflow residual
    // (min-height:100vh + chrome) que faz a roda "encadear" (scroll chaining) pro <html>
    // quando o scroll do card termina. Bloqueamos o overflow-y do <html> só enquanto esta
    // tela está montada (restaurado no destroy) → o card vira o único scroller vertical.
    document.documentElement.style.overflowY = 'hidden';
    // FULL-BLEED do card: zera o padding lateral do canvas (`--canvas-padding-x`, consumido pelo
    // `.mat-sidenav-content` em admin-event-sidebar.scss) → o CARD preenche a largura toda. O
    // HEADER reaplica o recuo via `--canvas-pad-x` (valor natural, ainda exposto) — ver o SCSS.
    document.documentElement.style.setProperty('--canvas-padding-x', '0px');
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.headerRafId);
    // Restaura o scroll vertical do documento e o padding lateral do canvas ao sair da tela.
    document.documentElement.style.overflowY = '';
    document.documentElement.style.removeProperty('--canvas-padding-x');
  }

  toggleFilters(): void {
    this.filtersVisible = !this.filtersVisible;
    if (this.filtersVisible) {
      this.columnsVisible = false;
      // Snapshot (deep copy) do estado atual (= último estado aplicado), base do
      // Descartar e da detecção de alteração (filtersDirty). Deep copy porque o
      // getRawValue compartilha as referências dos arrays dos multi-selects.
      const raw = this.rankingTable?.formFilters?.getRawValue();
      this.filtersSnapshot = raw ? JSON.parse(JSON.stringify(raw)) : null;
    }
  }

  // "Aplicar" (Mostrar submissões) do menu de filtros: reexecuta a consulta da
  // tabela com os filtros pendentes e fecha o modal.
  onFilterApply(): void {
    this.rankingTable.showSubmissions();
    this.filtersVisible = false;
  }

  // "Descartar": restaura o form ao snapshot de quando o menu abriu (sem reexecutar
  // a tabela — ela ainda mostra o último estado aplicado) e fecha o modal.
  onFilterDiscard(): void {
    if (this.filtersSnapshot && this.rankingTable?.formFilters) {
      this.rankingTable.formFilters.patchValue(this.filtersSnapshot);
    }
    this.filtersVisible = false;
  }

  // Abre/fecha o menu de colunas ancorado na origem informada (cada botão passa a
  // si mesmo). Clicar no mesmo botão que abriu → fecha; clicar em outro → reancora.
  toggleColumnsFrom(origin: CdkOverlayOrigin): void {
    if (this.columnsVisible && this.activeColumnsOrigin === origin) {
      this.columnsVisible = false;
      return;
    }
    this.activeColumnsOrigin = origin;
    this.columnsVisible = true;
    this.filtersVisible = false;
  }

  // Há submissões carregadas no evento? Independe do filtro atual. É falso nos dois
  // casos "sem submissões": (a) não há trilhas; (b) há trilhas mas nenhuma submissão —
  // ambos deixam submissionRanking = []. Se HÁ submissões mas o filtro atual zera a
  // lista, continua true (o usuário precisa dos controles pra limpar o filtro).
  // Gate dos controles do header: sem submissões, não há o que buscar/filtrar/colorir.
  get hasSubmissions(): boolean {
    return (this.rankingTable?.submissionRanking?.length ?? 0) > 0;
  }

  // Badge do botão: total de checkboxes marcados somando todas as opções
  // selecionadas nos multi-selects de filtro. colorScheme é modo de exibição
  // (single-select), não conta como filtro.
  get filterActiveCount(): number {
    const value = this.rankingTable?.formFilters?.value;
    if (!value) {
      return 0;
    }
    return [
      SubmissionsFilter.STATUS,
      SubmissionsFilter.TOPICS,
      SubmissionsFilter.TRACKS,
      SubmissionsFilter.SUBMISSION_GROUPS,
      SubmissionsFilter.TPC_GROUPS,
    ].reduce((count, key) => count + (Array.isArray(value[key]) ? value[key].length : 0), 0);
  }

  // Houve alteração nos filtros desde que o menu abriu (form atual != snapshot)?
  // Habilita "Atualizar lista"/"Descartar" e ativa o modo modal. Arrays comparados
  // sem depender da ordem; cobre todos os campos do form de filtros (inclui
  // colorScheme — também é uma alteração aplicável pela "Atualizar lista").
  get filtersDirty(): boolean {
    const snapshot = this.filtersSnapshot;
    const current = this.rankingTable?.formFilters?.getRawValue();
    if (!snapshot || !current) {
      return false;
    }
    const norm = (v: any) => JSON.stringify(Array.isArray(v) ? [...v].sort() : (v ?? null));
    return Object.keys(current).some(key => norm(current[key]) !== norm(snapshot[key]));
  }

  // Clique no backdrop (= fora do menu de filtros). Só fecha quando NÃO há alteração
  // pendente (botões desabilitados): aí o menu é descartável livremente. Havendo
  // alteração o menu é modal — sai só por Atualizar lista, Descartar ou Esc.
  onFilterBackdropClick(): void {
    if (this.filtersDirty) {
      return;
    }
    // Sem alteração: clicar fora descarta (no-op) e fecha, igual ao Esc.
    this.onFilterDiscard();
  }

  // Fecha o menu ao clicar fora (evento do CDK overlay). NÃO fecha se o clique foi
  // num dos botões-gatilho (o toggle cuida) nem dentro de outro overlay do CDK —
  // ex.: o painel aberto de um mat-select de filtro, que é um overlay separado e
  // seria "fora" do nosso painel.
  onOverlayOutsideClick(event: MouseEvent, menu: 'filters' | 'columns'): void {
    // Filtros: o clique-fora é tratado pelo backdrop (onFilterBackdropClick), que
    // decide fechar ou bloquear conforme haja filtro definido. Aqui é no-op.
    if (menu === 'filters') {
      return;
    }
    const target = event.target as HTMLElement;
    if (target.closest('.chair-header-icon-button--filter') || target.closest('.columns-menu-button') ||
        target.closest('.cdk-overlay-pane')) {
      return;
    }
    this.columnsVisible = false;
  }

  // Esc: no menu de FILTROS (modal) equivale a Descartar; no de COLUNAS, fecha e
  // devolve o foco ao botão-gatilho. Com um mat-select aberto dentro do modal, o
  // Material consome o Esc (stopPropagation) e só fecha o dropdown — este handler
  // nem é alcançado, então o modal continua aberto.
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }
    if (this.filtersVisible) {
      event.preventDefault();
      this.onFilterDiscard();
      const filterButton = this.elementRef.nativeElement.querySelector('.chair-header-icon-button--filter') as HTMLElement;
      filterButton?.focus();
      return;
    }
    if (this.columnsVisible) {
      event.preventDefault();
      this.columnsVisible = false;
      const button = this.elementRef.nativeElement.querySelector('.columns-menu-button') as HTMLElement;
      button?.focus();
    }
  }

}
